import { useState, useEffect, useCallback, useRef } from 'react';

const FRAPPE_URL = process.env.REACT_APP_FRAPPE_URL || 'http://pra.localhost:8000';
const HEALTH_CHECK_INTERVAL = 60000; // Check every 60 seconds
const HEALTH_CHECK_TIMEOUT = 50000; // 50 second timeout for health checks

// Singleton state - shared across all hook instances
let sharedState = {
  isOnline: navigator.onLine,
  isBackendAvailable: true,
  lastCheck: null,
  isChecking: false,
  healthCheckInterval: null,
  listeners: new Set(),
  initialized: false
};

/**
 * Notify all listeners of state change
 */
function notifyListeners() {
  sharedState.listeners.forEach(callback => callback(sharedState));
}

/**
 * Check if backend is actually reachable (singleton version)
 */
async function checkBackendHealthSingleton() {
  // Don't run multiple checks simultaneously
  if (sharedState.isChecking) {
    return;
  }

  sharedState.isChecking = true;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

    // Try to fetch a lightweight endpoint or the main page
    const response = await fetch(`${FRAPPE_URL}/api/method/ping`, {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal,
      // Don't cache health checks
      cache: 'no-cache'
    });

    clearTimeout(timeoutId);

    const available = response.ok || response.status === 404; // 404 is ok, means server is responding
    
    const previousAvailable = sharedState.isBackendAvailable;
    sharedState.isBackendAvailable = available;
    sharedState.lastCheck = new Date();

    if (available && !previousAvailable) {
      console.log('✅ [useOnlineStatus] Backend is now available - dispatching backend-available event');
      // Trigger sync event when backend comes back online
      window.dispatchEvent(new Event('backend-available'));
    } else if (!available && previousAvailable) {
      console.warn('⚠️ [useOnlineStatus] Backend is now unavailable - dispatching backend-unavailable event');
      window.dispatchEvent(new Event('backend-unavailable'));
    } else if (available) {
      console.log('✅ [useOnlineStatus] Backend health check passed (still available)');
    }

    notifyListeners();
    return available;
  } catch (error) {
    // Network error or timeout - backend is not available
    if (error.name === 'AbortError') {
      console.warn('⏱️ [useOnlineStatus] Backend health check timed out');
    } else {
      console.warn('❌ [useOnlineStatus] Backend health check failed:', error.message);
    }

    const previousAvailable = sharedState.isBackendAvailable;
    if (previousAvailable) {
      console.warn('⚠️ [useOnlineStatus] Backend became unavailable - dispatching backend-unavailable event');
      window.dispatchEvent(new Event('backend-unavailable'));
    }

    sharedState.isBackendAvailable = false;
    sharedState.lastCheck = new Date();
    notifyListeners();
    return false;
  } finally {
    sharedState.isChecking = false;
  }
}

/**
 * Initialize singleton health check (only once)
 */
function initializeHealthCheck() {
  if (sharedState.initialized) {
    return;
  }

  console.log('🔧 [useOnlineStatus] Initializing singleton health check manager');
  sharedState.initialized = true;

  // Listen for online/offline events
  const handleOnline = () => {
    sharedState.isOnline = true;
    console.log('📶 [useOnlineStatus] Network status: Online - checking backend health');
    notifyListeners();
    checkBackendHealthSingleton();
  };

  const handleOffline = () => {
    sharedState.isOnline = false;
    sharedState.isBackendAvailable = false;
    console.log('📵 [useOnlineStatus] Network status: Offline');
    notifyListeners();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Initial health check
  if (navigator.onLine) {
    console.log('🔧 [useOnlineStatus] Initial health check on mount');
    checkBackendHealthSingleton();
  }

  // Set up periodic health checks (only one interval for all instances)
  console.log(`🔧 [useOnlineStatus] Setting up periodic health checks (every ${HEALTH_CHECK_INTERVAL/1000}s)`);
  sharedState.healthCheckInterval = setInterval(() => {
    if (navigator.onLine) {
      checkBackendHealthSingleton();
    }
  }, HEALTH_CHECK_INTERVAL);
}

/**
 * Custom hook to track online/offline status with actual backend connectivity check
 * Uses singleton pattern to avoid multiple health check intervals
 * @returns {Object} - { isOnline: boolean, isBackendAvailable: boolean, lastCheck: Date }
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(sharedState.isOnline);
  const [isBackendAvailable, setIsBackendAvailable] = useState(sharedState.isBackendAvailable);
  const [lastCheck, setLastCheck] = useState(sharedState.lastCheck);
  const listenerRef = useRef(null);

  useEffect(() => {
    // Initialize singleton health check on first mount
    initializeHealthCheck();

    // Create listener for this component instance
    const listener = (state) => {
      setIsOnline(state.isOnline);
      setIsBackendAvailable(state.isBackendAvailable);
      setLastCheck(state.lastCheck);
    };

    // Register listener
    listenerRef.current = listener;
    sharedState.listeners.add(listener);
    console.log(`� [useOnlineStatus] Component mounted (${sharedState.listeners.size} active instances)`);

    // Cleanup on unmount
    return () => {
      if (listenerRef.current) {
        sharedState.listeners.delete(listenerRef.current);
        console.log(`🔧 [useOnlineStatus] Component unmounted (${sharedState.listeners.size} active instances)`);
      }
    };
  }, []);

  // Return both network online status and backend availability
  // The app should use isBackendAvailable for deciding whether to queue requests
  return {
    isOnline, // Network connection status
    isBackendAvailable, // Actual backend availability
    lastCheck, // Last health check timestamp
    checkHealth: checkBackendHealthSingleton // Manual health check trigger
  };
};